import { useState, useRef, useEffect } from 'react';
import { useHookExecutionTracker, createHookExecutionContext } from '@/utils/hookTestUtils';

/**
 * Props for the test component that will have its hooks analyzed
 */
interface TestComponentProps {
  hookExecutionContext: ReturnType<typeof createHookExecutionContext>;
  stateValue: string;
  triggerRerender: () => void;
}

/**
 * Sample component for demonstrating hook analysis
 * This component intentionally uses various hook patterns
 */
const TestComponent = ({ hookExecutionContext, stateValue, triggerRerender }: TestComponentProps) => {
  // Record hook executions using our tracker
  hookExecutionContext.recordExecution('useState:loading');
  const [isLoading, setIsLoading] = useState(false);
  
  hookExecutionContext.recordExecution('useState:data');
  const [data, setData] = useState<string[]>([]);
  
  hookExecutionContext.recordExecution('useRef');
  const renderCount = useRef(0);
  
  // First effect - always runs
  hookExecutionContext.recordExecution('useEffect:always');
  useEffect(() => {
    renderCount.current += 1;
    hookExecutionContext.recordExecution('useEffect:always:callback');
    
    return () => {
      hookExecutionContext.recordExecution('useEffect:always:cleanup');
    };
  });
  
  // Second effect - runs on state change
  hookExecutionContext.recordExecution('useEffect:state');
  useEffect(() => {
    hookExecutionContext.recordExecution('useEffect:state:callback');
    
    // Simulate data loading
    const timer = setTimeout(() => {
      setData([stateValue, 'item2', 'item3']);
      setIsLoading(false);
    }, 500);
    
    return () => {
      hookExecutionContext.recordExecution('useEffect:state:cleanup');
      clearTimeout(timer);
    };
  }, [stateValue]);
  
  // Third effect - runs conditionally based on loading state
  hookExecutionContext.recordExecution('useEffect:loading');
  useEffect(() => {
    if (isLoading) {
      hookExecutionContext.recordExecution('useEffect:loading:callback:true');
    } else {
      hookExecutionContext.recordExecution('useEffect:loading:callback:false');
    }
    
    return () => {
      hookExecutionContext.recordExecution('useEffect:loading:cleanup');
    };
  }, [isLoading]);
  
  return (
    <div className="p-4 border rounded bg-gray-50">
      <h3 className="text-lg font-semibold mb-2">Test Component</h3>
      <p>State Value: {stateValue}</p>
      <p>Render Count: {renderCount.current}</p>
      <p>Loading: {isLoading ? 'Yes' : 'No'}</p>
      <p>Data Items: {data.length}</p>
      
      <div className="mt-4">
        <button
          onClick={() => {
            setIsLoading(true);
            triggerRerender();
          }}
          className="px-3 py-1 bg-blue-500 text-white rounded mr-2"
        >
          Reload Data
        </button>
      </div>
    </div>
  );
};

/**
 * Hook Analyzer Component
 * 
 * A visual tool for analyzing and debugging React hook execution order.
 * Shows execution order and helps detect inconsistencies across renders.
 */
export function HookAnalyzer() {
  // Create context for tracking hook execution
  const contextRef = useRef(createHookExecutionContext());
  const context = contextRef.current;
  
  // Component state
  const [stateValue, setStateValue] = useState('initial');
  const [executionRecords, setExecutionRecords] = useState<string[]>([]);
  const [renderTrigger, setRenderTrigger] = useState(0);
  
  // Force component to re-render
  const triggerRerender = () => setRenderTrigger(prev => prev + 1);
  
  // Update execution records from context
  useEffect(() => {
    setExecutionRecords(context.getExecutionOrder());
  }, [renderTrigger, stateValue]);
  
  // Reset records
  const handleReset = () => {
    context.reset();
    setExecutionRecords([]);
  };
  
  // Change state value
  const handleChangeState = () => {
    setStateValue(prev => 
      prev === 'initial' ? 'changed' : 
      prev === 'changed' ? 'another' : 'initial'
    );
  };
  
  return (
    <div className="p-6 max-w-4xl mx-auto bg-white shadow-lg rounded-lg">
      <h2 className="text-2xl font-bold mb-6">React Hook Execution Analyzer</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <TestComponent 
            hookExecutionContext={context}
            stateValue={stateValue}
            triggerRerender={triggerRerender}
          />
          
          <div className="mt-4 space-x-2">
            <button
              onClick={handleChangeState}
              className="px-4 py-2 bg-purple-600 text-white rounded"
            >
              Change State ({stateValue})
            </button>
            
            <button
              onClick={triggerRerender}
              className="px-4 py-2 bg-green-600 text-white rounded"
            >
              Force Rerender
            </button>
            
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-red-600 text-white rounded"
            >
              Reset Records
            </button>
          </div>
        </div>
        
        <div className="border rounded p-4 bg-gray-50">
          <h3 className="text-lg font-semibold mb-2">Hook Execution Order</h3>
          <p className="text-sm text-gray-500 mb-2">
            Records: {executionRecords.length} | 
            Current State: {stateValue} |
            Renders: {renderTrigger}
          </p>
          
          <div className="h-96 overflow-y-auto bg-white border rounded p-2">
            <ol className="list-decimal pl-5">
              {executionRecords.map((record, index) => (
                <li 
                  key={index}
                  className={`py-1 ${
                    record.includes(':cleanup') 
                      ? 'text-red-600' 
                      : record.includes(':callback') 
                        ? 'text-blue-600'
                        : 'text-green-700'
                  }`}
                >
                  {record}
                </li>
              ))}
            </ol>
            
            {executionRecords.length === 0 && (
              <p className="text-gray-400 italic text-center mt-4">
                No hook executions recorded yet
              </p>
            )}
          </div>
        </div>
      </div>
      
      <div className="bg-amber-50 border border-amber-200 p-4 rounded">
        <h3 className="text-lg font-semibold mb-2">How to Use This Analyzer</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>The left panel contains a test component with various hooks</li>
          <li>The right panel shows the execution order of hooks</li>
          <li>Use the buttons below to trigger state changes and re-renders</li>
          <li>Watch how hooks execute in the same order on every render</li>
          <li>Green entries are hook declarations</li>
          <li>Blue entries are effect callbacks</li>
          <li>Red entries are cleanup functions</li>
        </ul>
      </div>
      
      <div className="mt-6 p-4 border rounded bg-blue-50">
        <h3 className="text-lg font-semibold mb-2">React Hook Rules Analysis</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-medium mb-1">Consistent Execution Order</h4>
            <p className="text-sm text-gray-600">
              Hooks should always execute in the same order on every render.
              This is why we don't use conditional hooks or early returns.
            </p>
          </div>
          
          <div>
            <h4 className="font-medium mb-1">Cleanup Functions</h4>
            <p className="text-sm text-gray-600">
              Cleanup functions run before the component unmounts or before
              the effect runs again due to dependency changes.
            </p>
          </div>
          
          <div>
            <h4 className="font-medium mb-1">Effect Dependencies</h4>
            <p className="text-sm text-gray-600">
              Missing dependencies can cause stale closures.
              Too many dependencies can cause excessive re-renders.
            </p>
          </div>
          
          <div>
            <h4 className="font-medium mb-1">Common Mistakes</h4>
            <p className="text-sm text-gray-600">
              Using hooks in conditionals, loops, or nested functions.
              Creating dynamic hooks with generators.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
